import { APIRoutes } from "@/api";
import { queryClient } from "@/lib/query-client";
import { ApiService } from "@/services/api/api-service";
import { PortfolioSchema } from "@/services/portfolios/schema";
import {
  QueryFunctionContext,
  UseQueryOptions,
  useQuery as useQueryTanstack,
} from "@tanstack/react-query";
import { useSession } from "next-auth/react";

async function getPortfolioDraft({
  queryKey,
  signal,
}: QueryFunctionContext<
  ReturnType<typeof PortfolioEditQuery.keys.portfolioEdit>
>) {
  const id = queryKey[1];
  if (!id) return null;

  const data = await fetch(`${APIRoutes.PORTFOLIO}${id}`, {
    cache: "no-cache",
    signal,
  }).then((res) => res.json());

  const result = ApiService.parseApiResponse(data, PortfolioSchema);

  if (result.status === "success") {
    return result.data;
  } else {
    return null;
  }
}

type TData = Awaited<ReturnType<typeof getPortfolioDraft>>;

export namespace PortfolioEditQuery {
  export const keys = {
    portfolioEdit: (id: string) => ["portfolio", id, "edit"] as const,
  };

  export function invalidate(id?: string) {
    if (!id) {
      return queryClient.invalidateQueries({
        queryKey: keys.portfolioEdit(""),
      });
    }
    return queryClient.invalidateQueries({
      queryKey: keys.portfolioEdit(id),
    });
  }

  export function useQuery(
    id = "",
    options?: Omit<
      UseQueryOptions<
        TData,
        unknown,
        TData,
        ReturnType<typeof keys.portfolioEdit>
      >,
      "queryKey" | "queryFn" | "enabled"
    >,
  ) {
    const { data: session } = useSession();

    return useQueryTanstack({
      queryKey: keys.portfolioEdit(id),
      queryFn: getPortfolioDraft,
      ...options,
      enabled: !!id && !!session?.user?.id,
    });
  }
}
